import React, { useMemo } from 'react';

import { IntegrationDomain, IntegrationVersion } from 'api/usePublisherIntegrationTest';
import { PROFILES_ENABLED_PLACEHOLDER } from 'constants/constants';
import { textModelOptions } from 'utils/selectOptionsUtils';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { IntegrationTestModal } from 'components/IntegrationTestModal/IntegrationTestModal';

import { docsUrls } from '../DeveloperSettings.constants';
import { IIntegrationSectionDependencies } from '../DeveloperSettings.integration';
import { EApiVersion, ESavingForm } from '../DeveloperSettings.types';
import { IVersionConfig } from '../DeveloperSettings.types';
import { useOtpForm } from '../hooks/useCallbackForms';
import { useIntegrationTestModal } from '../hooks/useIntegrationTestModal';

import { CallbackSection } from './CallbackSection';

type IInitiateGameAuthSectionProps = Pick<
  IIntegrationSectionDependencies,
  'publisherId' | 'integrationData' | 'updateIntegration' | 'getIntegration' | 'savingForm' | 'setSavingForm' | 'isProfilesEnabled'
>;

export const InitiateGameAuthSection: React.FC<IInitiateGameAuthSectionProps> = ({
  publisherId,
  integrationData,
  updateIntegration,
  getIntegration,
  savingForm,
  setSavingForm,
  isProfilesEnabled
}) => {
  const form = useOtpForm({
    integrationData,
    updateIntegration,
    getIntegration,
    setSavingForm
  });

  const testModal = useIntegrationTestModal({
    publisherId,
    domain: IntegrationDomain.INITIATE_GAME_AUTH,
    version: IntegrationVersion.V1,
    title: 'Initiate Game Auth Callback Test',
    callbackUrl: form.initialValues.otpGenerateDeeplinkUrl
  });

  const desktopDigitsItems = useMemo(
    () =>
      textModelOptions.map((option) => ({
        label: option.content?.toString() || option.value?.toString() || '',
        value: option.value?.toString() || ''
      })),
    []
  );

  const currentValue = useMemo(() => {
    if (isProfilesEnabled) {
      return PROFILES_ENABLED_PLACEHOLDER;
    }
    return form.values.otpGenerateDeeplinkUrl;
  }, [isProfilesEnabled, form.values.otpGenerateDeeplinkUrl]);

  const otpTextModelValue = useMemo(() => {
    if (isProfilesEnabled) {
      return PROFILES_ENABLED_PLACEHOLDER;
    }
    return form.values.otpTextModel;
  }, [isProfilesEnabled, form.values.otpTextModel]);

  const versionConfigs: IVersionConfig[] = [
    {
      version: EApiVersion.V1,
      label: EApiVersion.V1,
      value: currentValue,
      onChange: (value) => form.setFieldValue('otpGenerateDeeplinkUrl', value),
      error: form.touched.otpGenerateDeeplinkUrl
        ? (form.errors.otpGenerateDeeplinkUrl as string)
        : undefined,
      docsUrl: docsUrls.initiateGameAuth,
      afterUrlField: (
        <InputSelectDropdown
          options={desktopDigitsItems}
          defaultSelected={desktopDigitsItems.find((item) => item.value === otpTextModelValue)}
          onOptionChange={(value) => form.setFieldValue('otpTextModel', value)}
          label="Desktop Digits"
          disabled={isProfilesEnabled}
          placeholder="4 digits"
          className="w-[140px]"
        />
      )
    }
  ];

  const { schema, ...testModalProps } = testModal;

  return (
    <>
      <CallbackSection
        title="Initiate Game Auth Callback"
        versionConfigs={versionConfigs}
        onSave={form.submitForm}
        isSaveDisabled={!form.dirty || savingForm !== ESavingForm.NONE}
        isLoading={savingForm === ESavingForm.OTP}
        disabled={isProfilesEnabled}
        onTestClick={testModal.openModal}
      />
      {schema && (
        <IntegrationTestModal {...testModalProps} schema={schema} />
      )}
    </>
  );
};
